import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import type { Subscription } from '@/lib/firebase/subscribe'
import { unread } from '@/lib/inbox/items'
import { createRetainer } from '@/lib/live/retainer'
import {
  archiveInboxThread as repoArchive,
  archiveInboxThreads as repoArchiveMany,
  markAllInboxRead as repoMarkAllRead,
  markInboxRead as repoMarkRead,
  markInboxUnread as repoMarkUnread,
  subscribeInboxEntries,
  subscribeInboxReadMarks,
  subscribeInboxShellThreads,
  subscribeInboxThreads,
  unarchiveInboxThread as repoUnarchive,
} from '@/repositories/inbox'
import type { SetupScope } from '@/repositories/setup-docs'
import { useAuthStore } from '@/stores/auth'
import { useKeyedSubscription } from '@/stores/keyed-subscription'
import { currentSetupScope, useScopedSubscription } from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import { INBOX_TABS, type InboxReadMarks, type InboxTab, type InboxThread } from '@/types/inbox'
import { isLoaded, loading, type LoadState } from '@/types/load-state'

/** How many threads a tab loads at first and each time the list asks for more. */
export const INBOX_PAGE_SIZE = 30

/** The most thread ids one bulk call takes. */
const BULK_CHUNK = 500

type TabRecord<T> = Record<InboxTab, T>

function perTab<T>(make: () => T): TabRecord<T> {
  return { inbox: make(), sent: make(), archive: make() }
}

/** The mailbox: threads per tab, the open thread's messages, the member's read state and the shell's unread counts. */
export const useInboxStore = defineStore('inbox', () => {
  const auth = useAuthStore()

  const threads = ref<TabRecord<LoadState<InboxThread[]>>>(perTab(() => loading()))
  const limits = ref<TabRecord<number>>(perTab(() => INBOX_PAGE_SIZE))
  const threadSubs: TabRecord<Subscription<InboxThread[]> | null> = perTab(() => null)
  const threadStops: TabRecord<(() => void) | null> = perTab(() => null)

  function stopTab(tab: InboxTab): void {
    threadStops[tab]?.()
    threadStops[tab] = null
    threadSubs[tab]?.stop()
    threadSubs[tab] = null
  }

  /** Opens a listener at the tab's current limit; the one it replaces keeps feeding the list until the new one has answered. */
  function openTab(tab: InboxTab, scope: SetupScope): void {
    const previousSub = threadSubs[tab]
    const previousStop = threadStops[tab]
    const sub = subscribeInboxThreads(scope, tab, limits.value[tab])
    threadSubs[tab] = sub
    threadStops[tab] = watch(
      sub.state,
      (state) => {
        if (state.status === 'loading') return
        threads.value[tab] = state
        if (previousSub && previousSub !== sub) {
          previousStop?.()
          previousSub.stop()
        }
      },
      { immediate: true },
    )
  }

  const tabRetainers = perTab<ReturnType<typeof createRetainer> | null>(() => null)

  /** Keeps a tab's threads live while the returned release has not been called. */
  function retainTab(tab: InboxTab): () => void {
    tabRetainers[tab] ??= createRetainer({
      open() {
        const scope = currentSetupScope()
        if (scope) openTab(tab, scope)
        else
          threads.value[tab] = {
            status: 'error',
            message: i18n.global.t('common.noWorkspaceSelected'),
          }
      },
      close() {
        stopTab(tab)
        threads.value[tab] = loading()
        limits.value[tab] = INBOX_PAGE_SIZE
      },
    })
    return tabRetainers[tab].retain()
  }

  /** Raises a tab's limit by one page and reopens its listener. */
  function loadMore(tab: InboxTab): void {
    const scope = currentSetupScope()
    if (!scope || !hasMore.value[tab]) return
    limits.value[tab] += INBOX_PAGE_SIZE
    openTab(tab, scope)
  }

  const hasMore = computed<TabRecord<boolean>>(() => {
    const result = perTab(() => false)
    for (const tab of INBOX_TABS) {
      const state = threads.value[tab]
      result[tab] = isLoaded(state) && state.value.length >= limits.value[tab]
    }
    return result
  })

  const entriesSubscription = useKeyedSubscription((scope, threadId) =>
    subscribeInboxEntries(scope, threadId),
  )
  const entries = entriesSubscription.state

  /** Keeps one thread's messages live while the returned release has not been called. */
  const retainEntries = entriesSubscription.retain

  const readMarksSubscription = useScopedSubscription((scope) =>
    subscribeInboxReadMarks(scope, auth.user?.uid ?? ''),
  )

  const readMarks = computed<InboxReadMarks>(() =>
    isLoaded(readMarksSubscription.state.value)
      ? readMarksSubscription.state.value.value
      : new Map(),
  )

  const shellThreadsSubscription = useScopedSubscription(subscribeInboxShellThreads)

  /** The newest unarchived Inbox threads the shell watches; empty until loaded. */
  const shellThreads = computed<InboxThread[]>(() =>
    isLoaded(shellThreadsSubscription.state.value)
      ? shellThreadsSubscription.state.value.value
      : [],
  )

  /** Whether the shell's thread feed has answered, even with no threads. */
  const shellLoaded = computed(() => isLoaded(shellThreadsSubscription.state.value))

  const shellRetainer = createRetainer({
    open() {
      const scope = currentSetupScope()
      if (!scope || !auth.user) {
        shellThreadsSubscription.fail(i18n.global.t('common.noWorkspaceSelected'))
        return
      }
      shellThreadsSubscription.open(scope)
      readMarksSubscription.open(scope)
    },
    close() {
      shellThreadsSubscription.close()
      readMarksSubscription.close()
    },
  })
  onScopeDispose(() => {
    shellRetainer.dispose()
    for (const retainer of Object.values(tabRetainers)) retainer?.dispose()
  })

  /** Loaded threads of one tab that the member has not read. */
  function unreadThreads(tab: InboxTab): InboxThread[] {
    const state = threads.value[tab]
    return isLoaded(state) ? state.value.filter((thread) => unread(thread, readMarks.value)) : []
  }

  /** Unread threads among the shell's watched threads; the nav count. */
  const unreadCount = computed(
    () => shellThreads.value.filter((thread) => unread(thread, readMarks.value)).length,
  )

  /** Threads among the shell's watched threads that the Inbox flags as important. */
  const importantCount = computed(
    () => shellThreads.value.filter((thread) => thread.actionNeeded).length,
  )

  async function run<TInput>(
    action: (input: TInput) => Promise<unknown>,
    input: TInput,
  ): Promise<boolean> {
    try {
      await action(input)
      return true
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('inbox.toast.actionFailed'),
        parseCallableError(cause).message,
      )
      return false
    }
  }

  /** Marks a thread read for the member; a thread already read is left alone. */
  async function markRead(threadId: string): Promise<boolean> {
    const known = INBOX_TABS.flatMap((tab) => {
      const state = threads.value[tab]
      return isLoaded(state) ? state.value : []
    }).find((thread) => thread.id === threadId)
    if (known && !unread(known, readMarks.value)) return true
    return run(repoMarkRead, { threadId })
  }

  /** Marks a thread unread for the member. */
  function markUnread(threadId: string): Promise<boolean> {
    return run(repoMarkUnread, { threadId })
  }

  /** Marks threads read, in calls of at most 500 ids. */
  async function markReadMany(threadIds: readonly string[]): Promise<boolean> {
    for (let start = 0; start < threadIds.length; start += BULK_CHUNK) {
      const ok = await run(repoMarkAllRead, {
        threadIds: threadIds.slice(start, start + BULK_CHUNK),
      })
      if (!ok) return false
    }
    return true
  }

  /** Marks every loaded unread thread of a tab read. */
  function markAllRead(tab: InboxTab = 'inbox'): Promise<boolean> {
    return markReadMany(unreadThreads(tab).map((thread) => thread.id))
  }

  function archive(threadId: string): Promise<boolean> {
    return run(repoArchive, { threadId })
  }

  /** Archives threads, in calls of at most 500 ids. */
  async function archiveMany(threadIds: readonly string[]): Promise<boolean> {
    for (let start = 0; start < threadIds.length; start += BULK_CHUNK) {
      const ok = await run(repoArchiveMany, {
        threadIds: threadIds.slice(start, start + BULK_CHUNK),
      })
      if (!ok) return false
    }
    return true
  }

  function unarchive(threadId: string): Promise<boolean> {
    return run(repoUnarchive, { threadId })
  }

  return {
    threads,
    hasMore,
    entries,
    readMarks,
    shellThreads,
    shellLoaded,
    unreadCount,
    importantCount,
    retain: shellRetainer.retain,
    retainTab,
    retainEntries,
    loadMore,
    unreadThreads,
    markRead,
    markUnread,
    markReadMany,
    markAllRead,
    archive,
    archiveMany,
    unarchive,
  }
})
